import React, { useEffect, useMemo, useState } from 'react';
import {
  Dialog, DialogContent, DialogActions,
  Box, Typography, Button, Radio, RadioGroup, FormControlLabel, Chip, IconButton, Collapse,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import LocalText from '../common/LocalText';
import { fmtLocalDate, formatDateRanges } from '../../utils/dateUtils';

// Must match CampaignAdCleanupModes on the API.
export const CLEANUP_MODES = { PRUNE: 'prune_dates', DELETE_ALL: 'delete_all' };

// The ads an option touches: "delete all" removes every ad; "keep" only edits the ones that lose dates.
export const touchedAds = (impact, mode) => {
  const ads = impact?.ads ?? [];
  return mode === CLEANUP_MODES.DELETE_ALL ? ads : ads.filter((a) => a.isAffected);
};

// Ads that need a word even though no run date has to be removed (so no choice is offered):
//   needsReschedule  it had run dates, but none fall inside the new campaign dates, so it will not run until rescheduled
//   earlier          past run dates outside the new dates: they stay on record as history
// Every past date comes back from the API as history, so those inside the new dates are still "run dates" here.
export const headsUpAds = (impact) => {
  const start = String(impact?.newStartDate ?? '').slice(0, 10);
  const end   = String(impact?.newEndDate ?? '').slice(0, 10);
  return (impact?.ads ?? [])
    .map((ad) => {
      const removed = ad.removedDates ?? [];
      const kept    = ad.keptDates ?? [];
      const history = ad.historyDates ?? [];
      const earlier = history.filter((d) => d < start || d > end);
      const runsInNewDates = kept.length + (history.length - earlier.length);
      return { ad, earlier, needsReschedule: removed.length + kept.length + history.length > 0 && runsInNewDates === 0 };
    })
    .filter((x) => x.needsReschedule || x.earlier.length > 0);
};

// True when the dialog should open in its informational form: nothing to remove, but something the user should know.
export const needsHeadsUp = (impact) => impact?.hasImpact === false && headsUpAds(impact).length > 0;

// Only ids are sent: the API re-derives names and dates itself, so the approver sees what the database says.
export const buildAdCleanup = (impact, mode) => ({
  mode,
  ads: touchedAds(impact, mode).map((a) => ({ adId: a.adId })),
});

// ── Look: the CMS's standard blue dialogs ────────────────────────────────────────────────────────
const C = {
  navy:      '#0a326d',
  navyHover: '#0d3d85',
  blue:      '#5383b3',
  paleBlue:  '#f7faff',
  softBlue:  '#edf2fb',
  line:      '#dde6f0',
  rowLine:   '#eaf0f6',
  text:      '#1e3a5f',
  muted:     '#64748b',
  danger:    '#c62828',
  dangerDark:'#b71c1c',
};

// Thin scrollbar, same colours the other scrollable panels in the CMS use.
const thinScrollSx = {
  scrollbarWidth: 'thin',
  scrollbarColor: '#c7d8ec transparent',
  '&::-webkit-scrollbar': { width: 6, height: 6 },
  '&::-webkit-scrollbar-track': { backgroundColor: 'transparent' },
  '&::-webkit-scrollbar-thumb': { backgroundColor: '#c7d8ec', borderRadius: 4 },
  '&::-webkit-scrollbar-thumb:hover': { backgroundColor: C.blue },
};

const STATUS_STYLE = {
  approved: { bg: '#dcfce7', fg: '#15803d' },
  pending:  { bg: '#fef3c7', fg: '#b45309' },
  draft:    { bg: '#f1f5f9', fg: '#475569' },
  rejected: { bg: '#fee2e2', fg: '#b91c1c' },
};

const TONES = {
  removed: { bg: '#fef2f2', fg: '#b91c1c', border: '#fecaca' },
  kept:    { bg: '#f0fdf4', fg: '#15803d', border: '#bbf7d0' },
};

const adLabel = (ad) => ad.name || `Ad #${ad.adId}`;

const StatusChip = ({ status }) => {
  if (!status) return <Typography sx={{ fontSize: 12, color: C.muted }}>—</Typography>;
  const s = STATUS_STYLE[String(status).toLowerCase()] ?? STATUS_STYLE.draft;
  return (
    <Chip size="small" label={status} sx={{ height: 20, fontSize: 11, fontWeight: 600, bgcolor: s.bg, color: s.fg, textTransform: 'capitalize' }} />
  );
};

const DateChips = ({ title, dates, tone }) => {
  if (!dates.length) return null;
  return (
    <Box sx={{ mb: 1 }}>
      <Typography sx={{ fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.6, color: tone.fg, mb: 0.5 }}>
        {title} ({dates.length})
      </Typography>
      {/* Equal-width cells so the dates line up in straight columns instead of a ragged flow */}
      <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(108px, 1fr))', gap: 0.75 }}>
        {dates.map((d) => (
          <Chip
            key={d}
            size="small"
            label={fmtLocalDate(d) ?? d}
            sx={{
              width: '100%', height: 22, fontSize: 11, fontVariantNumeric: 'tabular-nums',
              bgcolor: tone.bg, color: tone.fg, border: `1px solid ${tone.border}`,
              '& .MuiChip-label': { px: 0.5, textAlign: 'center' },
            }}
          />
        ))}
      </Box>
    </Box>
  );
};

const HEAD_CELL_SX = {
  bgcolor: C.paleBlue, color: C.blue, fontSize: 11, fontWeight: 700,
  textTransform: 'uppercase', letterSpacing: 0.6, py: 0.9, borderBottom: `1px solid ${C.line}`,
};

const AdTableRow = ({ ad, mode, open, onToggle }) => {
  const deleting = mode === CLEANUP_MODES.DELETE_ALL;
  const removed  = ad.removedDates ?? [];
  const kept     = ad.keptDates ?? [];
  const history  = ad.historyDates ?? [];   // before today: the ad ran on these days, they are never changed
  const total    = removed.length + kept.length;

  return (
    <>
      <TableRow hover sx={{ '& td': { borderBottom: open ? 'none' : `1px solid ${C.rowLine}` } }}>
        <TableCell sx={{ py: 1 }}>
          <Typography sx={{ fontWeight: 700, fontSize: 13, color: C.navy }}>{adLabel(ad)}</Typography>
          {ad.hasPendingApproval && (
            <Chip size="small" label="Awaiting approval" sx={{ mt: 0.5, height: 18, fontSize: 10, fontWeight: 600, bgcolor: '#fef3c7', color: '#b45309' }} />
          )}
        </TableCell>
        <TableCell sx={{ py: 1 }}>
          {ad.placementKey
            ? <Chip size="small" variant="outlined" label={ad.placementKey} sx={{ height: 20, fontSize: 11 }} />
            : <Typography sx={{ fontSize: 12, color: C.muted }}>—</Typography>}
        </TableCell>
        <TableCell sx={{ py: 1 }}><StatusChip status={ad.contentStatus} /></TableCell>
        <TableCell sx={{ py: 1 }}>
          {deleting ? (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, flexWrap: 'wrap' }}>
              <Chip size="small" label="Will be deleted" sx={{ height: 20, fontSize: 11, fontWeight: 700, bgcolor: '#fee2e2', color: '#b91c1c' }} />
              <Typography sx={{ fontSize: 11, color: C.muted }}>{total} date{total === 1 ? '' : 's'}</Typography>
            </Box>
          ) : (
            <Typography sx={{ fontSize: 12, fontWeight: 600 }}>
              <Box component="span" sx={{ color: TONES.removed.fg }}>{removed.length} removed</Box>
              <Box component="span" sx={{ color: C.muted }}>{' · '}</Box>
              <Box component="span" sx={{ color: TONES.kept.fg }}>{kept.length} kept</Box>
            </Typography>
          )}
        </TableCell>
        <TableCell align="right" sx={{ py: 1 }}>
          <Button
            size="small"
            onClick={onToggle}
            aria-expanded={open}
            aria-label={`${open ? 'Hide' : 'View'} dates of ${adLabel(ad)}`}
            startIcon={open ? <KeyboardArrowUpIcon sx={{ fontSize: 16 }} /> : <VisibilityOutlinedIcon sx={{ fontSize: 16 }} />}
            sx={{ textTransform: 'none', fontSize: 12, fontWeight: 600, color: C.blue, minWidth: 0, '&:hover': { bgcolor: C.softBlue } }}
          >
            {open ? 'Hide' : 'View'}
          </Button>
        </TableCell>
      </TableRow>

      <TableRow>
        <TableCell colSpan={5} sx={{ p: 0, border: 0 }}>
          <Collapse in={open} unmountOnExit>
            <Box sx={{ px: 2, py: 1.5, bgcolor: C.paleBlue, borderBottom: `1px solid ${C.rowLine}`, maxHeight: 170, overflowY: 'auto', ...thinScrollSx }}>
              {total === 0 && history.length === 0 && (
                <Typography sx={{ fontSize: 12, color: C.muted }}>This ad has no run dates.</Typography>
              )}
              {total === 0 && history.length > 0 && (
                <Typography sx={{ fontSize: 12, color: C.muted }}>This ad has no upcoming run dates.</Typography>
              )}
              {deleting ? (
                <>
                  <DateChips title="All run dates" dates={[...removed, ...kept].sort()} tone={TONES.removed} />
                  {total > 0 && (
                    <Typography sx={{ fontSize: 11, color: C.muted }}>These dates are deleted together with the ad.</Typography>
                  )}
                </>
              ) : (
                <>
                  <DateChips title="Removed" dates={removed} tone={TONES.removed} />
                  <DateChips title="Kept" dates={kept} tone={TONES.kept} />
                </>
              )}
              {history.length > 0 && (
                <Typography sx={{ fontSize: 11, color: C.muted, mt: 0.5 }}>
                  {history.length} past date{history.length === 1 ? '' : 's'} stay as run history and are not changed: {formatDateRanges(history, 3)}.
                </Typography>
              )}
            </Box>
          </Collapse>
        </TableCell>
      </TableRow>
    </>
  );
};

const optionCardSx = (selected, accent) => ({
  alignItems: 'flex-start',
  m: 0,
  px: 1.5,
  py: 1.25,
  borderRadius: '12px',
  border: `1.5px solid ${selected ? accent : C.line}`,
  bgcolor: selected ? (accent === C.danger ? '#fef2f2' : C.softBlue) : 'white',
  transition: 'border-color 0.15s, background-color 0.15s',
  '&:hover': { borderColor: accent },
});

// Informational form: nothing is removed, the user only confirms they have seen what the move means for each ad.
const HeadsUpDialog = ({ open, impact, needsApproval, onCancel, onConfirm }) => {
  const items = headsUpAds(impact);
  return (
    <Dialog open={open} onClose={onCancel} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: '16px', overflow: 'hidden' } }}>
      <Box sx={{ bgcolor: C.navy, color: 'white', display: 'flex', alignItems: 'center', justifyContent: 'space-between', py: 2, px: 3 }}>
        <Typography component="span" sx={{ fontWeight: 700, fontSize: 16 }}>
          <LocalText>Review the new campaign dates</LocalText>
        </Typography>
        <IconButton size="small" aria-label="close" onClick={onCancel} sx={{ color: 'rgba(255,255,255,0.7)', '&:hover': { color: 'white' } }}>
          <CloseIcon fontSize="small" />
        </IconButton>
      </Box>

      <DialogContent dividers sx={{ p: 3, ...thinScrollSx }}>
        <Typography sx={{ fontSize: 13, color: C.text, mb: 2 }}>
          <LocalText>
            {`Moving this campaign to ${fmtLocalDate(impact?.newStartDate) ?? ''} – ${fmtLocalDate(impact?.newEndDate) ?? ''} does not remove any run dates. Please note:`}
          </LocalText>
        </Typography>

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, maxHeight: 320, overflowY: 'auto', ...thinScrollSx }}>
          {items.map(({ ad, earlier, needsReschedule }) => (
            <Box key={ad.adId} data-testid={`heads-up-ad-${ad.adId}`} sx={{ p: 1.5, borderRadius: '10px', border: `1px solid ${C.line}`, bgcolor: C.paleBlue }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                <Typography sx={{ fontWeight: 700, fontSize: 13, color: C.navy }}>{adLabel(ad)}</Typography>
                <StatusChip status={ad.contentStatus} />
              </Box>
              {needsReschedule && (
                <Box sx={{ display: 'flex', gap: 0.75, alignItems: 'flex-start', mb: earlier.length ? 0.5 : 0 }}>
                  <WarningAmberIcon sx={{ fontSize: 16, color: '#d97706', mt: '1px' }} />
                  <Typography sx={{ fontSize: 12, color: '#92400e' }}>
                    <LocalText>No run date falls inside the new campaign dates. This ad will not run until you schedule new run dates for it.</LocalText>
                  </Typography>
                </Box>
              )}
              {earlier.length > 0 && (
                <Box sx={{ display: 'flex', gap: 0.75, alignItems: 'flex-start' }}>
                  <InfoOutlinedIcon sx={{ fontSize: 16, color: C.blue, mt: '1px' }} />
                  <Typography sx={{ fontSize: 12, color: C.text }}>
                    <LocalText>Earlier run dates stay on record as history and are not changed:</LocalText>
                    {` ${formatDateRanges(earlier, 3)} (${earlier.length} day${earlier.length === 1 ? '' : 's'}).`}
                  </Typography>
                </Box>
              )}
            </Box>
          ))}
        </Box>

        {needsApproval && (
          <Box sx={{ mt: 2, p: 1.5, borderRadius: '10px', bgcolor: C.paleBlue, border: `1px solid ${C.line}`, display: 'flex', gap: 1, alignItems: 'flex-start' }}>
            <InfoOutlinedIcon sx={{ fontSize: 18, color: C.blue, mt: '1px' }} />
            <Typography sx={{ fontSize: 12.5, color: C.text }}>
              <LocalText>This change is sent for approval. No ad is changed.</LocalText>
            </Typography>
          </Box>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2, bgcolor: 'grey.50', gap: 1.5 }}>
        <Button onClick={onCancel} sx={{ color: '#85a5c6', fontWeight: 600, textTransform: 'none', borderRadius: '10px' }}>
          <LocalText>Cancel</LocalText>
        </Button>
        <Button
          variant="contained"
          onClick={() => onConfirm(null)}
          sx={{ bgcolor: C.navy, color: 'white', fontWeight: 700, textTransform: 'none', borderRadius: '10px', px: 3, boxShadow: 'none', '&:hover': { bgcolor: C.navyHover, boxShadow: 'none' } }}
        >
          <LocalText>Continue</LocalText>
        </Button>
      </DialogActions>
    </Dialog>
  );
};

/**
 * Shown when moving a campaign's dates would leave some of its ads with run dates outside the new range.
 * When nothing has to be removed but an ad is left with no run dates (or keeps earlier ones), it opens in an
 * informational form instead; onConfirm(null) then means "no cleanup to send".
 * `impact` is the response of getCampaignDateImpact. onConfirm(mode) receives 'prune_dates' | 'delete_all'.
 */
const CampaignDateImpactDialog = ({ open, impact, needsApproval = false, onCancel, onConfirm }) => {
  const [mode, setMode] = useState(CLEANUP_MODES.PRUNE);
  // Accordion: at most one ad's dates are expanded at a time.
  const [openAdId, setOpenAdId] = useState(null);

  useEffect(() => {
    if (open) { setMode(CLEANUP_MODES.PRUNE); setOpenAdId(null); }
  }, [open]);

  const ads      = impact?.ads ?? [];
  const affected = ads.filter((a) => a.isAffected);
  const touched  = useMemo(() => touchedAds(impact, mode), [impact, mode]);
  const pending  = touched.filter((a) => a.hasPendingApproval);
  const blocked  = pending.length > 0;
  const deleting = mode === CLEANUP_MODES.DELETE_ALL;

  const toggleAd = (adId) => setOpenAdId((current) => (current === adId ? null : adId));

  if (impact?.hasImpact === false) {
    return <HeadsUpDialog open={open} impact={impact} needsApproval={needsApproval} onCancel={onCancel} onConfirm={onConfirm} />;
  }

  return (
    <Dialog
      open={open}
      onClose={onCancel}
      maxWidth="md"
      fullWidth
      PaperProps={{ sx: { borderRadius: '16px', overflow: 'hidden' } }}
    >
      <Box sx={{ bgcolor: C.navy, color: 'white', display: 'flex', alignItems: 'center', justifyContent: 'space-between', py: 2, px: 3 }}>
        <Typography component="span" sx={{ fontWeight: 700, fontSize: 16 }}>
          <LocalText>Ads affected by the new campaign dates</LocalText>
        </Typography>
        <IconButton size="small" aria-label="close" onClick={onCancel} sx={{ color: 'rgba(255,255,255,0.7)', '&:hover': { color: 'white' } }}>
          <CloseIcon fontSize="small" />
        </IconButton>
      </Box>

      <DialogContent dividers sx={{ p: 3, ...thinScrollSx }}>
        <Typography sx={{ fontSize: 13, color: C.text, mb: 2 }}>
          <LocalText>
            {`Moving this campaign to ${fmtLocalDate(impact?.newStartDate) ?? ''} – ${fmtLocalDate(impact?.newEndDate) ?? ''} leaves run dates of ${affected.length} of its ${ads.length} ad${ads.length === 1 ? '' : 's'} outside the new dates. Choose what should happen:`}
          </LocalText>
        </Typography>

        <RadioGroup value={mode} onChange={(e) => setMode(e.target.value)} sx={{ gap: 1 }}>
          <FormControlLabel
            value={CLEANUP_MODES.PRUNE}
            control={<Radio size="small" sx={{ color: '#b6c6da', '&.Mui-checked': { color: C.navy } }} />}
            sx={optionCardSx(!deleting, C.navy)}
            label={
              <Box>
                <Typography sx={{ fontWeight: 700, fontSize: 13, color: C.navy }}><LocalText>Keep the ads and remove the out-of-range dates</LocalText></Typography>
                <Typography sx={{ fontSize: 12, color: C.blue }}>
                  <LocalText>Dates inside the new range stay. An ad left with no dates stays in the campaign with 0 days and has to be rescheduled.</LocalText>
                </Typography>
              </Box>
            }
          />
          <FormControlLabel
            value={CLEANUP_MODES.DELETE_ALL}
            control={<Radio size="small" sx={{ color: '#b6c6da', '&.Mui-checked': { color: C.danger } }} />}
            sx={optionCardSx(deleting, C.danger)}
            label={
              <Box>
                <Typography sx={{ fontWeight: 700, fontSize: 13, color: C.navy }}><LocalText>Delete all ads in this campaign</LocalText></Typography>
                <Typography sx={{ fontSize: 12, color: C.blue }}>
                  <LocalText>{`All ${ads.length} ad${ads.length === 1 ? '' : 's'} are deleted so you can create new ones.`}</LocalText>
                </Typography>
              </Box>
            }
          />
        </RadioGroup>

        <Typography sx={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.8, color: C.blue, mt: 2.5, mb: 0.75 }}>
          <LocalText>{deleting ? 'Ads that will be deleted' : 'Ads that lose run dates'}</LocalText>
        </Typography>

        <TableContainer sx={{ maxHeight: 300, border: `1px solid ${C.line}`, borderRadius: '10px', ...thinScrollSx }}>
          <Table
            size="small"
            stickyHeader
            aria-label="Affected ads"
            sx={{
              // vertical column lines; the expanded "dates" row is a single full-width cell, so it gets none
              '& th:not(:last-of-type)': { borderRight: `1px solid ${C.line}` },
              '& td:not(:last-of-type)': { borderRight: `1px solid ${C.rowLine}` },
            }}
          >
            <TableHead>
              <TableRow>
                <TableCell sx={HEAD_CELL_SX}>Ad</TableCell>
                <TableCell sx={HEAD_CELL_SX}>Placement</TableCell>
                <TableCell sx={HEAD_CELL_SX}>Status</TableCell>
                <TableCell sx={HEAD_CELL_SX}>Run dates</TableCell>
                <TableCell sx={HEAD_CELL_SX} align="right">Dates</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {touched.map((ad) => (
                <AdTableRow
                  key={ad.adId}
                  ad={ad}
                  mode={mode}
                  open={openAdId === ad.adId}
                  onToggle={() => toggleAd(ad.adId)}
                />
              ))}
            </TableBody>
          </Table>
        </TableContainer>

        {blocked && (
          <Box sx={{ mt: 2, p: 1.5, borderRadius: '10px', bgcolor: '#fffbeb', border: '1px solid #fde68a', display: 'flex', gap: 1, alignItems: 'flex-start' }}>
            <WarningAmberIcon sx={{ fontSize: 18, color: '#d97706', mt: '1px' }} />
            <Typography sx={{ fontSize: 12.5, color: '#92400e' }}>
              <LocalText>
                {`These ads are awaiting approval. Withdraw or resolve them before changing the campaign dates: ${pending.map(adLabel).join(', ')}`}
              </LocalText>
            </Typography>
          </Box>
        )}

        {needsApproval && !blocked && (
          <Box sx={{ mt: 2, p: 1.5, borderRadius: '10px', bgcolor: C.paleBlue, border: `1px solid ${C.line}`, display: 'flex', gap: 1, alignItems: 'flex-start' }}>
            <InfoOutlinedIcon sx={{ fontSize: 18, color: C.blue, mt: '1px' }} />
            <Typography sx={{ fontSize: 12.5, color: C.text }}>
              <LocalText>This change is sent for approval. The approver sees this list, and the ads are changed only when it is approved.</LocalText>
            </Typography>
          </Box>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2, bgcolor: 'grey.50', gap: 1.5 }}>
        <Button onClick={onCancel} sx={{ color: '#85a5c6', fontWeight: 600, textTransform: 'none', borderRadius: '10px' }}>
          <LocalText>Cancel</LocalText>
        </Button>
        <Button
          variant="contained"
          disabled={blocked}
          onClick={() => onConfirm(mode)}
          startIcon={deleting ? <DeleteOutlineIcon sx={{ fontSize: 17 }} /> : undefined}
          sx={{
            bgcolor: deleting ? C.danger : C.navy, color: 'white', fontWeight: 700, textTransform: 'none', borderRadius: '10px', px: 3,
            boxShadow: 'none', '&:hover': { bgcolor: deleting ? C.dangerDark : C.navyHover, boxShadow: 'none' },
            '&.Mui-disabled': { bgcolor: '#c8d8ea', color: 'white' },
          }}
        >
          <LocalText>{deleting ? 'Delete ads and continue' : 'Keep ads and continue'}</LocalText>
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CampaignDateImpactDialog;
